/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Box, Button, Popover, TextField } from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import React, { memo, useState } from "react";
import CustomErrorText from "../CustomErrorText";

interface Props {
  startValue: Dayjs | null;
  endValue: Dayjs | null;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched?: (field: string, value: boolean) => void;
  className?: string;
  touched?: { [key: string]: boolean };
  error?: { [key: string]: string };
  startName: string;
  endName: string;
  startLabel: string;
  endLabel: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

function CustomFreeDateRange({
  startValue,
  endValue,
  setFieldValue,
  setFieldTouched,
  className,
  touched,
  error,
  startName,
  endName,
  startLabel,
  endLabel,
  minDate,
  maxDate
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selecting, setSelecting] = useState<"start" | "end">("start");

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setFieldTouched?.(startName, true);
    setFieldTouched?.(endName, true);
  };

  const handleClear = () => {
    setFieldValue(startName, null);
    setFieldValue(endName, null);
    setSelecting("start");
    handleClose();
  };

  const dateDisplay = startValue
    ? `${startValue.format("DD/MM/YYYY")} - ${
        endValue ? endValue.format("DD/MM/YYYY") : "Select End"
      }`
    : "Select Date Range";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={`w-full ${className}`}>
        <div
          onClick={handleClick}
          className="bg-white border border-solid rounded-lg cursor-pointer border-discoBall focus-within:border-2 focus-within:border-hotShot"
        >
          <TextField
            fullWidth
            variant="filled"
            label={`${startLabel} - ${endLabel}`}
            value={dateDisplay}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
              classes: {
                input:
                  "!text-sm !leading-5 !font-medium !text-black !bg-white !rounded-lg !font-montserrat !cursor-pointer",
                root: "!rounded-lg !bg-white"
              }
            }}
            InputLabelProps={{
              shrink: true,
              classes: {
                root: "!text-sm !leading-5 !font-medium !text-platinumGranite !font-montserrat !pt-0.5 !pb-2",
                shrink: "!text-purpleComet !text-xs"
              }
            }}
          />
        </div>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box className="flex flex-col items-center p-2">
            <div className="flex w-full gap-2 mb-2">
              <Button
                fullWidth
                variant={selecting === "start" ? "contained" : "outlined"}
                onClick={() => setSelecting("start")}
                className={
                  selecting === "start"
                    ? "!bg-hotShot !text-white"
                    : "!text-hotShot !border-hotShot"
                }
              >
                Start
              </Button>
              <Button
                fullWidth
                variant={selecting === "end" ? "contained" : "outlined"}
                onClick={() => setSelecting("end")}
                className={
                  selecting === "end" ? "!bg-hotShot !text-white" : "!text-hotShot !border-hotShot"
                }
              >
                End
              </Button>
            </div>

            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={selecting === "start" ? startValue : endValue}
              minDate={selecting === "end" && startValue ? startValue : minDate}
              maxDate={maxDate}
              onChange={(newValue) => {
                if (selecting === "start") {
                  setFieldValue(startName, newValue);
                  setFieldValue(endName, null);
                  setSelecting("end");
                } else {
                  setFieldValue(endName, newValue);
                  handleClose();
                }
              }}
              slotProps={{ toolbar: { hidden: true }, actionBar: { actions: [] } }}
            />

            <div className="flex w-full gap-2 mt-2">
              <Button
                fullWidth
                variant="text"
                onClick={handleClear}
                className="!text-platinumGranite !font-montserrat !text-xs !normal-case"
              >
                Clear Filter
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleClose}
                className="!text-hotShot !font-montserrat !text-xs !normal-case !font-bold"
              >
                Done
              </Button>
            </div>
          </Box>
        </Popover>

        {(touched?.[startName] && error?.[startName]) ||
        (touched?.[endName] && error?.[endName]) ? (
          <CustomErrorText text={error?.[startName] || error?.[endName] || ""} type="error" />
        ) : null}
      </div>
    </LocalizationProvider>
  );
}

CustomFreeDateRange.defaultProps = {
  className: "",
  touched: {},
  error: {},
  setFieldTouched: () => {},
  minDate: undefined,
  maxDate: undefined
};

export default memo(CustomFreeDateRange);
